<script setup lang="ts">
// Vue
import { ref, onMounted, watch, computed } from "vue";

// Dependencies
import { v4 as uuidv4 } from "uuid";

// Components
import InputLabel from "./InputLabel.vue";
import Popover from "@/components/Reusables/Popover/Popover.vue";

const props = defineProps<{
  label?: string;
  hideChevron?: boolean;
  hideClear?: boolean;
  options: Array<{ name: string; value: any; isFavourite?: boolean }>;
  keepAfterSelect?: boolean;
  errorMsg?: string;
  required?: boolean;
  placeholder?: string;
  defaultValue?: string | number;
  /**
   * Makes the whole control inert: the text input is not typeable, the options panel never opens,
   * and the clear / chevron affordances are not rendered. Callers were already passing this
   * (BimCodeSelect, and through it the material modal's Classification field) while the prop did not
   * exist, so a read-only surface still had a fully working typeahead here (RTLCA-5585).
   */
  disabled?: boolean;

  /**
   * Optional + nullable external control of the text input.
   * If NOT provided, component behaves like it does today (uncontrolled).
   */
  modelValue?: string | null;
}>();

const emits = defineEmits<{
  (e: "optionSelected", value: any): void;
  (e: "update:modelValue", value: string | null): void;
}>();

const allOptions = ref<Array<any>>(props.options);
const options = ref<Array<any>>(props.options);
const currentlyHoveredIndex = ref<number>(0);
const id = ref<string>(uuidv4());
const showNarrowedOptions = ref<boolean>(false);

// Uncontrolled fallback
const internalSearchString = ref<string>("");

// Proxy: uses v-model when provided, otherwise internal
const searchString = computed<string>({
  get() {
    return props.modelValue ?? internalSearchString.value;
  },
  set(val) {
    // If parent provided modelValue, treat as controlled
    if (props.modelValue !== undefined) {
      emits("update:modelValue", val);
    } else {
      // Uncontrolled mode (current behavior)
      internalSearchString.value = val;
    }
  },
});

onMounted(async () => {
  // Set searchstring based on default value - but find the right option so the entire name is shown
  if (props.defaultValue !== undefined) {
    const defaultOption = props.options.find((option) => option.value === props.defaultValue);
    if (defaultOption) searchString.value = defaultOption.name;
  }
});

const getNewOptions = () => {
  const newOptions = allOptions.value.filter((option: any) => {
    if (option !== null && option.name.toLowerCase().includes(searchString.value.toLowerCase())) return true;
    return false;
  });
  options.value = newOptions;
};

watch(searchString, (_newSearchString, _oldSearchString) => {
  showNarrowedOptions.value = true;
  getNewOptions();
});

const realOptions = computed(() => {
  return showNarrowedOptions.value ? options.value : allOptions.value;
});

const handleNewSelected = (value: any) => {
  if (value === undefined) return;
  if (value === null) return;
  if (value === "") return;

  // Validate against options before sending
  if (!realOptions.value.find((option) => option.value === value)) return;

  emits("optionSelected", value);

  // Set input text to selected option's name
  searchString.value = realOptions.value.find((option) => option.value === value)?.name ?? "";
};

const nextItem = (event: any) => {
  if (event.keyCode !== 38 && event.keyCode !== 40) return;

  if (event.keyCode === 38) {
    if (currentlyHoveredIndex.value <= 0) return;
    currentlyHoveredIndex.value--;
  } else {
    if (currentlyHoveredIndex.value >= realOptions.value.length - 1) return;
    currentlyHoveredIndex.value++;
  }

  if (currentlyHoveredIndex.value % 4 === 0) {
    const focusedElement = document.getElementById(`${id.value}-${currentlyHoveredIndex.value}`);
    focusedElement?.scrollIntoView({ behavior: "smooth" });
  }
};

const deleteSearchString = () => {
  showNarrowedOptions.value = false;
  searchString.value = "";
};

watch(
  () => props.options,
  (newOptions) => {
    allOptions.value = newOptions;
    options.value = newOptions;
    currentlyHoveredIndex.value = 0;

    // If a defaultValue is provided and found in the new options, restore it.
    // This handles async option loads (e.g. classification codes fetched after mount)
    // where the pre-selected value would otherwise be wiped.
    if (props.defaultValue !== undefined) {
      const defaultOption = newOptions.find((option) => option.value === props.defaultValue);
      if (defaultOption) {
        searchString.value = defaultOption.name;
        return;
      }
    }

    // keep today's behavior: reset search when options change
    searchString.value = "";
  },
  { deep: true, immediate: true }
);
</script>

<template>
  <div class="w-full">
    <div class="flex flex-col">
      <div class="mb-1">
        <InputLabel
          :id="id"
          :label="label"
          :required="required" />
      </div>

      <!-- The options list lives in the shared Popover reusable, which handles outside-click / Esc
           dismissal, the new panel styling, and the transition. The input drives open state on
           focus / typing; returnFocus is disabled so closing on select doesn't refocus-and-reopen.
           Teleported to <body> so the panel isn't clipped by (or stacked under) a scrollable/overflow
           ancestor such as a modal's tab panel + footer; matchTriggerWidth keeps it input-width and
           panelZ sits it at the modal layer so it floats above modal chrome. -->
      <Popover
        placement="bottom-start"
        :return-focus="false"
        teleport
        match-trigger-width
        panel-z="z-modal"
        panel-class="max-h-52 overflow-y-auto"
        transition="fade-top-quick">
        <template #trigger="{ open, close }">
          <div class="relative w-full">
            <input
              :id="id ? id : `input-${id}`"
              v-model="searchString"
              type="text"
              name="input"
              :class="[
                errorMsg ? 'border-app-red-500' : 'border-slate-300',
                disabled ? 'text-type-muted cursor-not-allowed bg-slate-200' : 'bg-slate-0',
              ]"
              autocomplete="off"
              :disabled="disabled"
              :placeholder="placeholder ? placeholder : undefined"
              class="focus:border-app-blue-600 rounded-8 h-9 w-full truncate border p-2 pr-8 text-sm leading-none outline-hidden"
              @keyup="
                (event) => {
                  if (disabled) return;
                  if (event.key !== 'Escape' && event.key !== 'Enter') open();
                  nextItem(event);
                }
              "
              @keyup.enter="
                () => {
                  if (disabled) return;
                  handleNewSelected(realOptions[currentlyHoveredIndex]?.value);
                  close();
                }
              "
              @focusin="
                () => {
                  if (disabled) return;
                  showNarrowedOptions = false;
                  open();
                }
              " />

            <!-- Both affordances are actions, so a disabled control renders neither: a clear "×" that
                 does nothing reads as broken rather than as read-only. -->
            <div
              v-if="!disabled"
              class="bottom-px-3 top-px-3 bg-slate-0 absolute right-2 flex cursor-pointer items-center text-xs">
              <div
                v-if="!hideClear && searchString.length > 0"
                class="w-px-24 p-px-4 flex items-center justify-center"
                @click="
                  () => {
                    deleteSearchString();
                    open();
                  }
                ">
                <font-awesome-icon icon="fa-solid fa-times" />
              </div>

              <!-- Not gated on an empty search string: a pre-filled select is never empty, so the chevron would never show. -->
              <div
                v-else-if="!hideChevron"
                class="w-px-24 p-px-4 flex items-center justify-center"
                @click="open">
                <font-awesome-icon icon="fa-solid fa-chevron-down" />
              </div>
            </div>
          </div>
        </template>

        <template #default="{ close }">
          <div
            v-for="(option, index) in realOptions"
            :id="`${id}-${index}`"
            :key="option.value"
            class="t-xs flex w-full cursor-pointer gap-2 p-2 text-sm"
            :class="{
              'bg-slate-100': index === currentlyHoveredIndex,
              'bg-app-yellow-50': option.isFavorite,
            }"
            data-cy="search-select-option"
            @mouseenter="currentlyHoveredIndex = index"
            @click="
              () => {
                handleNewSelected(option.value);
                close();
              }
            ">
            <font-awesome-icon
              v-if="option.isFavorite"
              class="text-app-yellow-400"
              :icon="'fa-solid fa-star'" />
            <span>{{ option.name }}</span>
          </div>
        </template>
      </Popover>
    </div>

    <span
      v-if="errorMsg"
      class="text-app-red-700 text-xs"
      >{{ errorMsg }}</span
    >
  </div>
</template>
